"use client";

import {
  useCallback,
  useEffect,
  useState,
  useSyncExternalStore,
  useTransition,
} from "react";
import { Bell } from "lucide-react";
import { subscribePush, unsubscribePush } from "@/app/actions/push-subscription";

export type PushDeviceSubscriptionState = {
  supported: boolean;
  subscribed: boolean;
  endpoint: string | null;
};

type Props = {
  isLoggedIn: boolean;
  onDeviceStateChange?: (state: PushDeviceSubscriptionState) => void;
  readOnly?: boolean;
};

const subscribeToStaticBrowserCapability = () => () => {};
const getPushSupportSnapshot = () =>
  typeof window !== "undefined" &&
  typeof navigator !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window;
const getUnsupportedSnapshot = () => false;

export function PushSubscribeButton({
  isLoggedIn,
  onDeviceStateChange,
  readOnly = false,
}: Props) {
  const [subscribed, setSubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const supported = useSyncExternalStore(
    subscribeToStaticBrowserCapability,
    getPushSupportSnapshot,
    getUnsupportedSnapshot
  );
  const publishDeviceState = useCallback(
    (nextSubscribed: boolean, endpoint: string | null) => {
      onDeviceStateChange?.({
        supported,
        subscribed: nextSubscribed,
        endpoint,
      });
    },
    [onDeviceStateChange, supported],
  );

  useEffect(() => {
    if (readOnly) return;
    if (!supported || !isLoggedIn) {
      publishDeviceState(false, null);
      return;
    }

    let cancelled = false;
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        if (cancelled) return;
        setSubscribed(!!sub);
        publishDeviceState(!!sub, sub?.endpoint ?? null);
      });
    });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, publishDeviceState, readOnly, supported]);

  useEffect(() => {
    if (readOnly) return;
    if (!supported) return;
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  }, [readOnly, supported]);

  if (!supported || !isLoggedIn) return null;

  const handleToggle = () => {
    if (readOnly) return;
    setError(null);

    if (subscribed) {
      startTransition(async () => {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) {
          const endpoint = sub.endpoint;
          await sub.unsubscribe();
          await unsubscribePush(endpoint);
        }
        setSubscribed(false);
        publishDeviceState(false, null);
      });
    } else {
      startTransition(async () => {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY,
          });
          const json = sub.toJSON();
          const result = await subscribePush({
            endpoint: sub.endpoint,
            keys: {
              p256dh: json.keys?.p256dh ?? "",
              auth: json.keys?.auth ?? "",
            },
          });
          if (result.success) {
            setSubscribed(true);
            publishDeviceState(true, sub.endpoint);
          } else {
            setError(result.error ?? "Failed to enable notifications");
          }
        } catch {
          setError("Push notifications blocked. Check browser settings.");
        }
      });
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={handleToggle}
        disabled={readOnly || isPending}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-sm px-4 py-2.5 text-[13px] font-semibold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-teal disabled:opacity-50 ${
          subscribed
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-navy text-white hover:bg-navy-hover"
        }`}
      >
        <Bell className="h-4 w-4" aria-hidden="true" />
        {subscribed ? "Notifications enabled" : "Enable push notifications"}
      </button>
      {error && <p role="alert" className="mt-1 text-[11px] text-red-600">{error}</p>}
    </div>
  );
}
